import { isAxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import type { JWTPayload } from '@typeDefs/domain/auth';
import apiClient from '@services/api/client';
import { RefreshFailureReason } from '@generated/api';

const PROACTIVE_REFRESH_MARGIN_MS = 60_000; // 60 seconds before expiry

function extractRefreshFailureReason(error: unknown): RefreshFailureReason {
  const axiosError = error as { response?: { data?: { errorCode?: RefreshFailureReason } } };
  const code = axiosError?.response?.data?.errorCode;
  if (
    code === RefreshFailureReason.IdleTimeout ||
    code === RefreshFailureReason.Expired ||
    code === RefreshFailureReason.ReplayDetected ||
    code === RefreshFailureReason.SessionSuperseded
  ) {
    return code;
  }
  return RefreshFailureReason.Unknown;
}

function isRetryableRefreshFailure(error: unknown): boolean {
  return isAxiosError(error) && (!error.response || error.response.status >= 500);
}

// The server may have rotated the cookie even though its response never arrived; a retry from the
// same browser presents the old cookie inside the server's rotation grace window. A 401 is final.
async function requestRefresh() {
  try {
    return await apiClient.api.authRefreshCreate({});
  } catch (error) {
    if (!isRetryableRefreshFailure(error)) throw error;
    console.warn('Token refresh got no usable response; retrying once:', error);
    return apiClient.api.authRefreshCreate({});
  }
}

let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<boolean> | null = null;

function clearRefreshTimer(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(jwt: string): void {
  clearRefreshTimer();

  try {
    const decoded = jwtDecode<JWTPayload>(jwt);
    if (!decoded.exp) return;

    const expiresAtMs = decoded.exp * 1000;
    const delay = expiresAtMs - Date.now() - PROACTIVE_REFRESH_MARGIN_MS;

    if (delay <= 0) return; // Already expired or too close to expiry

    refreshTimer = setTimeout(() => {
      tokenManager.attemptRefresh();
    }, delay);
  } catch (error) {
    console.error('Failed to decode JWT for proactive refresh:', error);
  }
}

export const tokenManager = {
  getAccessToken(): string | null {
    return accessToken;
  },

  /**
   * Stores the new access token in memory and schedules proactive refresh.
   *
   * The refresh token is no longer accepted here — it is delivered by the server
   * as an httpOnly cookie and managed entirely by the browser. Keeping it out of
   * JS memory eliminates the XSS attack surface on the refresh token.
   */
  setTokens(newAccessToken: string): void {
    accessToken = newAccessToken;
    scheduleProactiveRefresh(newAccessToken);
  },

  clearTokens(): void {
    accessToken = null;
    clearRefreshTimer();
    refreshPromise = null;
    // Refresh token cookie is cleared server-side on logout (/api/auth/logout).
    // We intentionally do not touch it here — JS cannot access httpOnly cookies.
  },

  /**
   * Attempts to refresh the session using the httpOnly refresh token cookie.
   *
   * @param silent - When true, suppresses the session-expired event and telemetry
   *   on failure. Use this for the initial mount check where no prior session
   *   existed — a 401 there just means "not logged in", not "session terminated".
   *   Defaults to false so mid-session expiry (proactive timer, 401 interceptor)
   *   always dispatches the event and shows the appropriate banner.
   * @param announcedWhenSilent - Failure reasons that still dispatch the event and
   *   telemetry when `silent` is true.
   */
  async attemptRefresh(
    silent = false,
    announcedWhenSilent: readonly RefreshFailureReason[] = [],
  ): Promise<boolean> {
    const announces = (reason: RefreshFailureReason) =>
      !silent || announcedWhenSilent.includes(reason);

    // Mutex: if a refresh is already in-flight, return the existing promise
    if (refreshPromise !== null) {
      return refreshPromise;
    }

    // No pre-flight check for the refresh token — it lives in an httpOnly cookie
    // that JS cannot read. The server will return 401 if no valid cookie is present.

    refreshPromise = (async () => {
      try {
        // Empty body: the refresh token is sent automatically by the browser
        // via the httpOnly cookie on /api/auth. withCredentials ensures the
        // cookie is included on cross-origin requests in development.
        const response = await requestRefresh();

        const data = response.data;
        if (data.token) {
          // New refresh token cookie is set by the server (rotation).
          // We only need to store the access token in memory.
          tokenManager.setTokens(data.token);
          return true;
        }

        // 200 OK without an access token — server-side bug or schema drift; treat as session loss.
        tokenManager.clearTokens();
        const fallbackReason: RefreshFailureReason =
          (data.errorCode as RefreshFailureReason | undefined) ?? RefreshFailureReason.Unknown;
        if (announces(fallbackReason)) {
          window.dispatchEvent(
            new CustomEvent('session-expired', { detail: { errorCode: fallbackReason } }),
          );
        }
        return false;
      } catch (error) {
        const errorCode = extractRefreshFailureReason(error);
        console.error('Token refresh failed:', errorCode);
        if (announces(errorCode)) {
          if (errorCode === RefreshFailureReason.ReplayDetected) {
            // Warning-severity telemetry for incident response visibility — see
            // docs/2026-04-22-idle-auto-logout.md §6. Subscribers (e.g. Sentry)
            // should never log the token, raw email, or query strings.
            window.dispatchEvent(
              new CustomEvent('telemetry-event', {
                detail: { kind: 'refresh.replay_detected', severity: 'warning' },
              }),
            );
          }
          window.dispatchEvent(new CustomEvent('session-expired', { detail: { errorCode } }));
        }
        tokenManager.clearTokens();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  },

  isRefreshing(): boolean {
    return refreshPromise !== null;
  },

  waitForRefresh(): Promise<boolean> | null {
    return refreshPromise;
  },
};
